<template>
  <div class="user-projects-archive" data-testid="user-projects-archive-view">
    <div class="user-projects-archive__hero">
      <div class="user-projects-archive__hero-text">
        <h1 class="user-projects-archive__title">
          {{ $t('UserProjects.archive.title') }}
        </h1>
        <p v-if="selectedProject" class="user-projects-archive__subtitle">
          {{ $t('UserProjects.archive.subtitle', {
            project: selectedProject.name,
            count: entryCount,
          }) }}
        </p>
      </div>
      <div class="user-projects-archive__search-wrap">
        <i class="pi pi-search user-projects-archive__search-icon" aria-hidden="true" />
        <InputText
          :model-value="searchQuery"
          class="user-projects-archive__search"
          :placeholder="$t('UserProjects.archive.searchPlaceholder')"
          data-testid="archive-search"
          @update:model-value="onSearchInput"
        />
      </div>
    </div>

    <Message v-if="!hasProjectContext && userProjectContextReady" severity="info" :closable="false">
      {{ $t('UserProjects.context.required') }}
    </Message>

    <Message v-if="error" severity="error" :closable="false">
      {{ error }}
    </Message>

    <div v-if="loading || !userProjectContextReady" class="user-projects-archive__loading">
      <i class="pi pi-spin pi-spinner mr-2" />
      {{ $t('UserProjects.archive.loading') }}
    </div>

    <template v-else-if="hasProjectContext">
      <div class="user-projects-archive__filters" data-testid="archive-filter-bar">
        <div class="user-projects-archive__filter-group">
          <span class="user-projects-archive__filter-label">
            {{ $t('UserProjects.archive.filters.type') }}:
          </span>
          <div class="user-projects-archive__type-pills" data-testid="archive-type-filters">
            <button
              v-for="option in typeOptions"
              :key="option.value"
              type="button"
              class="user-projects-archive__type-pill"
              :class="{ 'user-projects-archive__type-pill--active': typeFilter === option.value }"
              :data-testid="`archive-type-${option.value}`"
              @click="setTypeFilter(option.value)"
            >
              {{ option.label }}
            </button>
          </div>
        </div>

        <div class="user-projects-archive__filter-group">
          <span class="user-projects-archive__filter-label">
            {{ $t('UserProjects.archive.filters.period') }}:
          </span>
          <Select
            :model-value="periodFilter"
            :options="periodOptions"
            option-label="label"
            option-value="value"
            class="user-projects-archive__period-select"
            data-testid="archive-period-filter"
            @update:model-value="setPeriodFilter"
          />
        </div>

        <button
          type="button"
          class="user-projects-archive__refresh"
          data-testid="archive-refresh"
          :disabled="loading"
          @click="refresh"
        >
          <i class="pi pi-refresh" :class="{ 'pi-spin': loading }" aria-hidden="true" />
        </button>
      </div>

      <div
        v-if="isEmpty"
        class="user-projects-archive__empty"
        data-testid="archive-empty"
      >
        <div class="user-projects-archive__empty-title">{{ $t('UserProjects.archive.emptyTitle') }}</div>
        <div class="user-projects-archive__empty-hint">{{ $t('UserProjects.archive.emptyHint') }}</div>
      </div>

      <ArchiveEntryFeed
        v-else
        :groups="groupedEntries"
        :user-lookup="userLookup"
        :milestone-lookup="milestoneLookup"
        @select-entry="openDrawer"
      />
    </template>

    <ArchiveDetailDrawer
      v-model:visible="drawerVisible"
      :entry="selectedEntry"
      :user-lookup="userLookup"
      :milestone-lookup="milestoneLookup"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import InputText from 'primevue/inputtext';
import Message from 'primevue/message';
import Select from 'primevue/select';
import ArchiveEntryFeed from '@/components/CustomerApp/UserProjects/ArchiveEntryFeed.vue';
import ArchiveDetailDrawer from '@/components/CustomerApp/UserProjects/ArchiveDetailDrawer.vue';
import {
  ARCHIVE_PERIOD_OPTIONS,
  ARCHIVE_TYPE_FILTER_VALUES,
} from '@/utils/userProjectsArchive';
import { buildUserLookup, getUserDisplayName, normalizeParticipantUsers } from '@/utils/userParticipants';
import NetworkContent from '@/helpers/networking/NetworkContent';

export default {
  name: 'UserProjectsArchiveView',
  components: {
    InputText,
    Message,
    Select,
    ArchiveEntryFeed,
    ArchiveDetailDrawer,
  },
  data() {
    return {
      participantUsers: [],
      drawerVisible: false,
      selectedEntry: null,
    };
  },
  computed: {
    ...mapGetters('pmProject', {
      selectedProjectId: 'selectedProjectId',
      selectedProject: 'selectedProject',
      hasUserProjectContext: 'hasUserProjectContext',
      userProjectContextReady: 'userProjectContextReady',
    }),
    ...mapGetters('pmArchive', {
      typeFilter: 'typeFilter',
      periodFilter: 'periodFilter',
      searchQuery: 'searchQuery',
      loading: 'loading',
      error: 'error',
      entryCount: 'entryCount',
      isEmpty: 'isEmpty',
      groupedEntries: 'groupedEntries',
      entries: 'entries',
    }),
    hasProjectContext() {
      return this.hasUserProjectContext;
    },
    typeOptions() {
      return ARCHIVE_TYPE_FILTER_VALUES.map((value) => ({
        value,
        label: this.$t(`UserProjects.archive.filters.types.${value}`),
      }));
    },
    periodOptions() {
      return ARCHIVE_PERIOD_OPTIONS.map((option) => ({
        value: option.value,
        label: this.$t(`UserProjects.archive.filters.periods.${option.value}`),
      }));
    },
    userLookup() {
      const lookup = buildUserLookup(this.participantUsers);
      return Object.fromEntries(
        Object.entries(lookup).map(([id, user]) => [id, getUserDisplayName(user) || id]),
      );
    },
    milestoneLookup() {
      const lookup = {};
      (this.entries || []).forEach((entry) => {
        if (entry.milestoneId && entry.milestoneTitle) {
          lookup[entry.milestoneId] = entry.milestoneTitle;
        }
        if (entry.entityType === 'milestones') {
          lookup[entry.entityId] = entry.title;
        }
      });
      return lookup;
    },
  },
  watch: {
    selectedProjectId: {
      immediate: true,
      handler(projectId) {
        this.loadArchiveEntries(projectId);
      },
    },
  },
  async mounted() {
    await this.loadParticipantUsers();
  },
  methods: {
    ...mapActions('pmArchive', [
      'fetchArchiveEntries',
      'setTypeFilter',
      'setPeriodFilter',
      'setSearchQuery',
      'refresh',
    ]),
    async loadArchiveEntries(projectId) {
      await this.fetchArchiveEntries(projectId);
    },
    async loadParticipantUsers() {
      try {
        const { data } = await NetworkContent.getParticipants();
        this.participantUsers = normalizeParticipantUsers(data?.users ?? []);
      } catch {
        this.participantUsers = [];
      }
    },
    onSearchInput(value) {
      this.setSearchQuery(value);
    },
    openDrawer(entry) {
      this.selectedEntry = entry;
      this.drawerVisible = true;
    },
  },
};
</script>

<style scoped>
.user-projects-archive {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.user-projects-archive__hero {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.user-projects-archive__title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: var(--color-dark);
}

.user-projects-archive__subtitle {
  margin: 0.25rem 0 0;
  font-size: 14px;
  color: #6b6b6b;
}

.user-projects-archive__search-wrap {
  position: relative;
  width: min(280px, 100%);
}

.user-projects-archive__search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #ababab;
  font-size: 0.875rem;
  pointer-events: none;
}

.user-projects-archive__search {
  width: 100%;
  padding-left: 2.25rem;
  min-height: 2.5rem;
  border-radius: 10px;
}

.user-projects-archive__filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1.5rem 2rem;
  padding: 1rem;
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 14px;
}

.user-projects-archive__filter-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}

.user-projects-archive__filter-label {
  font-size: 12px;
  font-weight: 500;
  color: #6b6b6b;
}

.user-projects-archive__type-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.user-projects-archive__type-pill {
  border: none;
  background: transparent;
  color: #6b6b6b;
  font-size: 12px;
  font-weight: 500;
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.user-projects-archive__type-pill:hover {
  background: #f5f5f5;
}

.user-projects-archive__type-pill--active {
  background: #fdf0e6;
  color: var(--color-orange);
}

.user-projects-archive__period-select {
  min-width: 10rem;
}

.user-projects-archive__refresh {
  margin-left: auto;
  width: 2rem;
  height: 2rem;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  background: #fff;
  color: var(--color-dark);
  cursor: pointer;
}

.user-projects-archive__refresh:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.user-projects-archive__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem 0;
  color: var(--color-disabled);
}

.user-projects-archive__empty {
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 14px;
  padding: 2rem;
  text-align: center;
  color: var(--color-disabled);
}

.user-projects-archive__empty-title {
  font-weight: 500;
  color: var(--color-dark);
}

.user-projects-archive__empty-hint {
  margin-top: 0.25rem;
  font-size: 0.875rem;
}
</style>
